/**
 * @file Reference-value resolution Drawer for a staged mapping review: one
 * row per distinct normalized key of a reference column (unresolved-first,
 * counted over the non-rejected rows within the import cap), each with a
 * named `ReferenceSelect` entity picker (manual text entry doubles as "save
 * as plain text") and a strength Tag. The Tag differentiates reference
 * strength: a resolved entity shows the band-coloured match confidence (or a
 * blue "Linked (manual)" badge for user-set links), with a tooltip carrying
 * the match basis ("matched '…' → record, NN%"); an unresolved key shows
 * "No match" when the model found no candidate, or "Weak guess NN%" when its
 * top candidate sits below the high band. A plain-text override shows "Plain
 * text". Clearing the picker is an explicit decision: it persists a plain-text
 * override of the extracted display value; re-picking the model's record in
 * the dropdown is the revert. Bulk action converts every unresolved key to
 * its plain-text copy. Every editable control freezes while an approval is
 * being written (`useMappingReviewSubmitting`) — the drawer is part of the
 * data-loss window. Filter state is controlled by the caller
 * (`MappingReviewColumns`), which also supplies the entity metadata.
 */
import { FC } from 'react';
import { Button, Drawer, Segmented, Tag, Tooltip, Typography, theme } from 'antd';
import { LinkOutlined, UserOutlined } from '@ant-design/icons';
import { useSnapshot } from 'valtio';
import type {
  ArCellValue,
  ArEntityReference,
  ArManualEntryValue,
  ReferenceCandidate,
  TableReferenceValueResolution,
} from '#types';
import ReferenceSelect from '@/components/ArTable/components/ArCell/ReferenceSelect';
import FlexCol from '../../atoms/FlexCol';
import { setResolutionOverride } from './mappingReviewSession';
import type { MappingReviewTableSlice } from './mappingReviewState';
import { mappingReviewConfidence } from './mappingReviewConfidence';
import { mappingReviewLabels } from './mappingReviewLabels';
import { useMappingReviewSubmitting } from './useMappingReviewSubmitting';
import { referenceKeyOccurrences } from './referenceKeyOccurrences';

const { Text, Title } = Typography;

/** Grid for the resolution table — flexible value/record tracks plus a fixed strength track so the trailing column aligns across rows. */
const REF_VALUE_GRID = 'minmax(160px, 1fr) minmax(350px, 1.6fr) 130px';

type ReferenceFilter = 'all' | 'unresolved' | 'manual' | 'matched';

interface DistinctValue {
  key: string;
  display: string;
  count: number;
  resolution: TableReferenceValueResolution | undefined;
}

function isEntityCellValue(value: ArCellValue): value is ArEntityReference {
  return (
    value != null
    && typeof value === 'object'
    && 'referenceType' in value
    && (value as ArEntityReference).referenceType === 'entity'
  );
}

function isManualCellValue(value: ArCellValue): value is ArManualEntryValue {
  return (
    value != null
    && typeof value === 'object'
    && 'valueType' in value
    && (value as ArManualEntryValue).valueType === 'manual'
  );
}

function resolutionToCellValue(resolution: TableReferenceValueResolution | undefined): ArCellValue {
  if (!resolution) return null;
  if (resolution.kind === 'entity') {
    return {
      referenceType: 'entity',
      docId: resolution.docId,
      ...(resolution.cacheLabel ? { cacheLabel: resolution.cacheLabel } : {}),
    };
  }
  return { valueType: 'manual', value: resolution.text };
}

const ResolutionTag: FC<{
  resolution: TableReferenceValueResolution | undefined;
  candidates: readonly ReferenceCandidate[] | undefined;
  display: string;
}> = ({ resolution, candidates, display }) => {
  if (!resolution) {
    const top = candidates?.[0];
    if (!top) {
      return (
        <Tag color="warning" style={{ margin: 0 }}>
          {mappingReviewLabels.noMatch}
        </Tag>
      );
    }
    const guess = mappingReviewConfidence(top.probability);
    return (
      <Tag color={guess.high ? 'warning' : guess.color} style={{ margin: 0 }}>
        {mappingReviewLabels.weakGuess(guess.label)}
      </Tag>
    );
  }
  if (resolution.kind === 'manual') {
    return <Tag style={{ margin: 0 }}>{mappingReviewLabels.plainText}</Tag>;
  }
  const cacheLabel = resolution.cacheLabel ?? resolution.docId;
  const view = resolution.confidence !== undefined ? mappingReviewConfidence(resolution.confidence) : undefined;
  const basis = view
    ? `matched '${display}' → ${cacheLabel}, ${view.label}`
    : `matched '${display}' → ${cacheLabel}`;
  return (
    <Tooltip title={basis}>
      {view ? (
        <Tag color={view.color} style={{ margin: 0 }}>
          {view.label}
        </Tag>
      ) : (
        <Tag color="blue" icon={<UserOutlined />} style={{ margin: 0 }}>
          {mappingReviewLabels.linkedManual}
        </Tag>
      )}
    </Tooltip>
  );
};

interface MappingReviewReferenceValuesProps {
  slice: MappingReviewTableSlice;
  sourceColumn: string;
  entityId: string;
  entityGroupId?: string;
  targetLabel: string;
  filter: ReferenceFilter;
  onFilterChange: (filter: ReferenceFilter) => void;
  onClose: () => void;
}

const MappingReviewReferenceValues: FC<MappingReviewReferenceValuesProps> = ({
  slice,
  sourceColumn,
  entityId,
  entityGroupId,
  targetLabel,
  filter,
  onFilterChange,
  onClose,
}) => {
  const { token } = theme.useToken();
  const snap = useSnapshot(slice);
  const submitting = useMappingReviewSubmitting();
  const columnSnap = snap.columns.find((c) => c.sourceColumn === sourceColumn);
  const baseColumn = snap.baseColumns.find((c) => c.sourceColumn === sourceColumn);
  const chunks = snap.chunks;
  /** Ranked candidates only apply while the live column still maps to the base target the model judged. */
  const candidatesByKey =
    baseColumn && columnSnap?.targetField === baseColumn.targetField ? baseColumn.candidates : undefined;

  const byKey = referenceKeyOccurrences({
    chunks,
    sourceColumn,
    cellOverrides: snap.cellOverrides,
    rejected: new Set(Object.keys(snap.rejectedRows).map(Number)),
    rowLimit: snap.baseRows.length,
  });

  const distinct: DistinctValue[] = [...byKey.entries()]
    .map(([key, { display, count }]) => ({
      key,
      display,
      count,
      resolution: columnSnap?.resolution?.[key] as TableReferenceValueResolution | undefined,
    }))
    .sort((a, b) => Number(Boolean(a.resolution)) - Number(Boolean(b.resolution)) || b.count - a.count);

  const unresolved = distinct.filter((d) => !d.resolution);
  const visible = distinct.filter((d) => {
    if (filter === 'unresolved') return !d.resolution;
    if (filter === 'manual') return d.resolution?.kind === 'manual';
    if (filter === 'matched') return d.resolution?.kind === 'entity';
    return true;
  });

  const resolve = (target: DistinctValue, value: ArCellValue) => {
    if (isEntityCellValue(value)) {
      setResolutionOverride(slice.groupId, sourceColumn, target.key, {
        kind: 'entity',
        docId: value.docId,
        ...(value.cacheLabel ? { cacheLabel: value.cacheLabel } : {}),
      });
      return;
    }
    if (isManualCellValue(value)) {
      setResolutionOverride(slice.groupId, sourceColumn, target.key, { kind: 'manual', text: value.value ?? '' });
      return;
    }
    setResolutionOverride(slice.groupId, sourceColumn, target.key, { kind: 'manual', text: target.display });
  };

  return (
    <Drawer
      open
      size={680}
      onClose={onClose}
      title={
        <Title level={5} style={{ margin: 0 }}>
          <LinkOutlined /> Resolve &quot;{sourceColumn}&quot; → {targetLabel}
        </Title>
      }
      zIndex={token.zIndexPopupBase + 3}
      aria-label={`Resolve reference values for ${sourceColumn}`}
    >
      <FlexCol gap={10}>
        <Segmented
          size="small"
          aria-label="Filter reference values"
          value={filter}
          onChange={(value) => onFilterChange(value as ReferenceFilter)}
          options={[
            { value: 'all', label: `all (${distinct.length})` },
            { value: 'unresolved', label: `unresolved (${unresolved.length})` },
            { value: 'manual', label: mappingReviewLabels.plainText },
            { value: 'matched', label: 'linked' },
          ]}
        />
        <div style={{ overflowX: 'auto' }}>
          <FlexCol noGrow noGap>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: REF_VALUE_GRID,
                columnGap: 10,
                alignItems: 'center',
                paddingBottom: 5,
              }}
            >
              <Text strong style={{ fontSize: token.fontSizeSM }}>Value</Text>
              <Text strong style={{ fontSize: token.fontSizeSM }}>Linked record</Text>
              <Text strong style={{ fontSize: token.fontSizeSM }}>Strength</Text>
            </div>
            {visible.map((d) => (
              <div
                key={d.key}
                style={{
                  display: 'grid',
                  gridTemplateColumns: REF_VALUE_GRID,
                  columnGap: 10,
                  alignItems: 'start',
                  paddingBlock: 5,
                }}
              >
                <FlexCol noGap noGrow>
                  <Text>&quot;{d.display}&quot;</Text>
                  <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                    applies to {d.count} row{d.count !== 1 ? 's' : ''}
                  </Text>
                </FlexCol>
                <div
                  role="group"
                  title={d.display}
                  aria-label={`Resolve value '${d.display}' (${d.count} rows)`}
                >
                  <ReferenceSelect
                    key={`${sourceColumn}-${d.key}`}
                    entityId={entityId}
                    entityGroupId={entityGroupId}
                    value={resolutionToCellValue(d.resolution)}
                    candidates={candidatesByKey?.[d.key] ? [...candidatesByKey[d.key]] : undefined}
                    onChange={(value) => resolve(d, value)}
                    disabled={submitting}
                    style={{ width: '100%' }}
                  />
                </div>
                <div>
                  <ResolutionTag resolution={d.resolution} candidates={candidatesByKey?.[d.key]} display={d.display} />
                </div>
              </div>
            ))}
          </FlexCol>
        </div>
        {visible.length === 0 && (
          <Text type="secondary">No values match this filter.</Text>
        )}
        <Button
          size="small"
          disabled={submitting || unresolved.length === 0}
          aria-label={`Set all unresolved values of ${sourceColumn} to plain text`}
          onClick={() => {
            for (const d of unresolved) {
              setResolutionOverride(slice.groupId, sourceColumn, d.key, { kind: 'manual', text: d.display });
            }
          }}
        >
          {mappingReviewLabels.setAllPlainText}
        </Button>
      </FlexCol>
    </Drawer>
  );
};

export default MappingReviewReferenceValues;
